import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchCampaignSurveys,
  deleteCampaignSurvey,
  exportCampaignSurveyCsv,
} from '../../api/client';
import formatDate from '../../utils/formatDate';
import './CampaignSurveyList.css';

const TOP_CONCERN_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'jobs', label: 'Jobs' },
  { value: 'roads', label: 'Roads' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'cost_of_living', label: 'Cost of Living' },
  { value: 'corruption', label: 'Corruption' },
  { value: 'other', label: 'Other' },
];

const VOTE_INTENT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'undecided', label: 'Undecided' },
];

function approvalBadgeClass(value) {
  if (value == null) return '';
  if (value >= 4) return 'badge-positive';
  if (value >= 3) return 'badge-pending';
  return 'badge-negative';
}

function voteBadgeClass(intent) {
  if (intent === 'yes') return 'badge-positive';
  if (intent === 'no') return 'badge-negative';
  if (intent === 'undecided') return 'badge-pending';
  return 'badge-neutral';
}

function outcomeBadgeClass(outcome) {
  if (outcome === 'completed') return 'badge-positive';
  if (outcome === 'partially_completed') return 'badge-pending';
  return 'badge-negative';
}

function humanize(value) {
  if (!value) return '—';
  return value.replace(/_/g, ' ');
}

export default function CampaignSurveyList() {
  const [surveys, setSurveys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const [topConcernFilter, setTopConcernFilter] = useState('');
  const [voteIntentFilter, setVoteIntentFilter] = useState('');
  const limit = 20;

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit, skip };
      if (topConcernFilter) params.top_concern = topConcernFilter;
      if (voteIntentFilter) params.vote_intent = voteIntentFilter;
      const res = await fetchCampaignSurveys(params);
      setSurveys(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip, topConcernFilter, voteIntentFilter]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteCampaignSurvey(id);
        setConfirmId(null);
        loadSurveys();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportCampaignSurveyCsv({
        top_concern: topConcernFilter,
        vote_intent: voteIntentFilter,
      });
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading && surveys.length === 0) {
    return <div className="loading">Loading campaign surveys...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load campaign surveys: {error}</div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Campaign Survey Responses</h3>
        <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      {exportError && (
        <div className="error-message" style={{ marginBottom: '0.5rem' }}>
          Export failed: {exportError}
        </div>
      )}
      <div className="campaign-survey-filter">
        <label htmlFor="campaign-concern-filter">Top concern:</label>
        <select
          id="campaign-concern-filter"
          value={topConcernFilter}
          onChange={(e) => { setTopConcernFilter(e.target.value); setSkip(0); }}
        >
          {TOP_CONCERN_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label htmlFor="campaign-vote-filter" style={{ marginLeft: '0.75rem' }}>Vote intent:</label>
        <select
          id="campaign-vote-filter"
          value={voteIntentFilter}
          onChange={(e) => { setVoteIntentFilter(e.target.value); setSkip(0); }}
        >
          {VOTE_INTENT_OPTIONS.map((o) => (
            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {surveys.length === 0 ? (
        <p>No campaign survey responses yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Phone</th>
              <th>Top Concern</th>
              <th>Approval</th>
              <th>Vote Intent</th>
              <th>Outcome</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {surveys.map((s) => (
              <React.Fragment key={s._id}>
                <tr onClick={() => toggleExpand(s._id)} style={{ cursor: 'pointer' }}>
                  <td>{formatDate(s.created_at)}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {s.phone_number || 'N/A'}
                  </td>
                  <td>{s.top_concern ? (
                    <span className="badge badge-neutral">{humanize(s.top_concern)}</span>
                  ) : '—'}</td>
                  <td>
                    {s.approval_score != null ? (
                      <span className={`badge ${approvalBadgeClass(s.approval_score)}`}>
                        {s.approval_score} / 5
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {s.vote_intent ? (
                      <span className={`badge ${voteBadgeClass(s.vote_intent)}`}>
                        {humanize(s.vote_intent)}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <span className={`badge ${outcomeBadgeClass(s.call_outcome)}`}>
                      {humanize(s.call_outcome || 'unknown')}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    {confirmId === s._id ? (
                      <>
                        <button className="btn-danger-sm" onClick={(e) => handleDelete(e, s._id)}>Confirm</button>
                        {' '}
                        <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                      </>
                    ) : (
                      <button className="btn-icon" onClick={(e) => handleDelete(e, s._id)} title="Delete">&#128465;</button>
                    )}
                  </td>
                </tr>
                {expandedId === s._id && (
                  <tr className="campaign-survey-detail-row">
                    <td colSpan={7}>
                      <div className="campaign-survey-details">
                        <div className="campaign-survey-meta">
                          <span className="detail-label">Call ID:</span>{' '}
                          <span className="detail-value">{s.call_id || 'N/A'}</span>
                        </div>

                        {s.accountability_weight && (
                          <div className="campaign-survey-section">
                            <div className="campaign-survey-section-label">Accountability Weight</div>
                            <div>{humanize(s.accountability_weight)}</div>
                          </div>
                        )}

                        {s.free_text_message && (
                          <div className="campaign-survey-section">
                            <div className="campaign-survey-section-label">Message to the President</div>
                            <div className="campaign-survey-text">{s.free_text_message}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((prev) => Math.max(0, prev - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {total === 0 ? 0 : skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((prev) => prev + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
